import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Container, Card, Button, Modal, Icon } from '../../../../core/ui';
import { getSharedReceipt, getSharedReceiptImageUrl } from '../../api/shared';
import styles from './SharedReceiptPage.module.css';

const SharedReceiptPage = () => {
  const { shareToken } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const response = await getSharedReceipt(shareToken);
        setReceipt(response.data);
      } catch (err) {
        setError('Receipt not found or link has expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchReceipt();
  }, [shareToken]);

  const formatCurrency = (amount) => {
    if (amount == null || isNaN(amount)) return '-';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Container className={styles.container}>
        <Card className={styles.errorCard}>
          <Card.Body className={styles.center}>
            <Icon name="fa-info-circle" size={48} className={styles.errorIcon} />
            <h4>{error}</h4>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className={styles.container}>
      <Card className={styles.receiptCard}>
        <Card.Header className={styles.cardHeader}>
          <div className={styles.storeInfo}>
            <Icon name="fa-store" size="lg" className={styles.me2} />
            <h4 className={styles.mb0}>{receipt.storeName}</h4>
          </div>
          <div className={styles.meta}>
            <span>
              <Icon name="fa-user" size="sm" className={styles.me1} />
              {receipt.ownerName}
            </span>
            <span>
              <Icon name="fa-calendar-alt" size="sm" className={styles.me1} />
              {formatDate(receipt.purchaseDateTime)}
            </span>
            {receipt.receiptNumber && (
              <span>
                <Icon name="fa-hashtag" size="sm" className={styles.me1} />
                {receipt.receiptNumber}
              </span>
            )}
          </div>
        </Card.Header>
        <Card.Body className={styles.cardBody}>
          <table className={styles.itemsTable}>
            <thead>
              <tr>
                <th>Product</th>
                <th className={styles.textCenter}>Qty</th>
                <th className={styles.textEnd}>Unit Price</th>
                <th className={styles.textEnd}>Total</th>
              </tr>
            </thead>
            <tbody>
              {receipt.items.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.productName}</td>
                  <td className={styles.textCenter}>{item.quantity}</td>
                  <td className={styles.textEnd}>{formatCurrency(item.unitPrice)}</td>
                  <td className={styles.textEnd}>{formatCurrency(item.totalPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {receipt.discounts && receipt.discounts.length > 0 && (
            <div className={styles.discountsSection}>
              <h6>Discounts</h6>
              {receipt.discounts.map((d, idx) => (
                <div key={idx} className={styles.discountRow}>
                  <span>{d.description}</span>
                  <span className={styles.discountAmount}>-{formatCurrency(d.amount)}</span>
                </div>
              ))}
            </div>
          )}

          <div className={styles.totals}>
            <div className={styles.totalRow}>
              <span>Subtotal</span>
              <span>{formatCurrency(receipt.subtotal)}</span>
            </div>
            <div className={styles.totalRow}>
              <span>Tax</span>
              <span>{formatCurrency(receipt.taxAmount)}</span>
            </div>
            {receipt.totalDiscounts > 0 && (
              <div className={styles.totalRow}>
                <span>Discounts</span>
                <span className={styles.discountAmount}>-{formatCurrency(receipt.totalDiscounts)}</span>
              </div>
            )}
            <div className={`${styles.totalRow} ${styles.grandTotal}`}>
              <span>Total</span>
              <span>{formatCurrency(receipt.totalAmount)}</span>
            </div>
          </div>
        </Card.Body>
        <Card.Footer className={styles.cardFooter}>
          <div className={styles.footerRow}>
            <small>Shared via fis4j</small>
            <Button variant="secondary" size="sm" onClick={() => setShowImageModal(true)}>
              <Icon name="fa-image" size="sm" className={styles.me1} />
              View Receipt Image
            </Button>
          </div>
        </Card.Footer>
      </Card>

      <Modal
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        title={receipt.storeName}
        size="lg"
      >
        <div className={styles.center}>
          <img
            src={getSharedReceiptImageUrl(shareToken)}
            alt="Receipt"
            className={styles.modalImage}
            onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<p class="text-muted">Receipt image not available</p>'; }}
          />
        </div>
      </Modal>
    </Container>
  );
};

export default SharedReceiptPage;
